import { useEffect, useState, type FormEvent } from 'react';
import { systemAdmins, type SystemAdminSummary } from './system.api';
import { useSystemAuth } from './SystemAuthContext';
import { Panel, ErrorNote, Field, inputClass, buttonClass } from './SystemLayout';

/** Platform operators. Creating one here is audited; a hand-written INSERT is not. */
export default function OperatorsPage() {
  const { admin } = useSystemAuth();
  const [rows, setRows] = useState<SystemAdminSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  async function load() {
    setError(null);
    try {
      setRows(await systemAdmins.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load operators');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const activeCount = rows.filter((r) => r.status === 'ACTIVE').length;

  async function toggle(row: SystemAdminSummary) {
    const next = row.status === 'ACTIVE' ? 'DEACTIVATED' : 'ACTIVE';
    if (next === 'DEACTIVATED' && !window.confirm(
      `Deactivate ${row.email}?\n\nEvery session they currently hold ends immediately.`,
    )) return;

    setError(null);
    try {
      setRows(await systemAdmins.setStatus(row.id, next));
    } catch (err) {
      // The API refuses self-deactivation and the last active operator.
      setError(err instanceof Error ? err.message : 'Could not change the status');
    }
  }

  return (
    <div className="space-y-5">
      <ErrorNote message={error} />

      <Panel
        title={`Operators${rows.length > 0 ? ` (${activeCount} active of ${rows.length})` : ''}`}
        action={
          <button type="button" onClick={() => setCreating((v) => !v)} className={buttonClass}>
            {creating ? 'Cancel' : 'New operator'}
          </button>
        }
      >
        {creating && <CreateForm onDone={() => { setCreating(false); void load(); }} />}

        {loading ? (
          <p className="text-sm text-slate-400">Loading...</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 font-medium">Operator</th>
                <th className="py-2 font-medium">Second factor</th>
                <th className="py-2 font-medium">Last sign-in</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isMe = row.id === admin?.id;
                return (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5">
                      <span className="text-slate-900">{row.displayName}</span>
                      {isMe && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                      <span className="block text-xs text-slate-500">{row.email}</span>
                    </td>
                    <td className="py-2.5 text-slate-400">
                      {row.totpEnrolled ? 'Enrolled' : <span className="text-slate-400">Not set up</span>}
                    </td>
                    <td className="py-2.5 text-slate-400">
                      {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : <span className="text-slate-400">Never</span>}
                    </td>
                    <td className="py-2.5">
                      <span className={
                        row.status === 'ACTIVE'
                          ? 'rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700'
                          : 'rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-400'
                      }>
                        {row.status === 'ACTIVE' ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td className="py-2.5 text-right">
                      {/* Hidden rather than disabled for your own row: the API
                          refuses self-deactivation outright, so offering the
                          button at all would only produce an error. */}
                      {!isMe && (
                        <button type="button" onClick={() => void toggle(row)}
                          className="text-xs font-medium text-slate-400 underline-offset-2 hover:text-slate-900 hover:underline">
                          {row.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await systemAdmins.create({ email, displayName, password });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the operator');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}
          className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <ErrorNote message={error} />
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Email">
          <input required type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Display name">
          <input required value={displayName}
                 onChange={(e) => setDisplayName(e.target.value)} className={inputClass} />
        </Field>
        <Field label="Password - at least 12 characters">
          <input required type="password" minLength={12} value={password}
                 onChange={(e) => setPassword(e.target.value)} className={inputClass} />
        </Field>
      </div>
      {/* No email invitation on purpose: a link that grants cross-organisation
          power is a phishing target, so the credential is handed over directly. */}
      <p className="text-[11px] text-slate-500">
        There is no invitation email. Set the password here and give it to them yourself,
        then have them change it from the console.
      </p>
      <button type="submit" disabled={busy} className={buttonClass}>
        {busy ? 'Creating...' : 'Create operator'}
      </button>
    </form>
  );
}
